import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "./useWorkspace";
import { useUserRole } from "./useUserRole";
import { tierLimits, type TierLimits } from "@/lib/tier";

const monthKey = () => new Date().toISOString().slice(0, 7);

export const useUsage = () => {
  const { active } = useWorkspace();
  const { isAdmin } = useUserRole();
  const [excerptCount, setExcerptCount] = useState(0);
  const [downloadsThisMonth, setDownloadsThisMonth] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    const [{ count }, { data: counter }] = await Promise.all([
      supabase.from("excerpts").select("id", { count: "exact", head: true }).eq("workspace_id", active.id),
      supabase.from("usage_counters").select("downloads_count").eq("workspace_id", active.id).eq("month", monthKey()).maybeSingle(),
    ]);
    setExcerptCount(count ?? 0);
    setDownloadsThisMonth(counter?.downloads_count ?? 0);
    setLoading(false);
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const baseLimits = tierLimits(active?.account_tier as Parameters<typeof tierLimits>[0]);
  // Admins always uncapped regardless of workspace tier.
  const limits: TierLimits = isAdmin ? { excerpts: null, downloadsPerMonth: null } : baseLimits;

  const downloadsAtLimit = limits.downloadsPerMonth !== null && downloadsThisMonth >= limits.downloadsPerMonth;


  const incrementDownload = useCallback(async () => {
    if (!active) return;
    const month = monthKey();
    const { data: existing } = await supabase
      .from("usage_counters").select("*")
      .eq("workspace_id", active.id).eq("month", month).maybeSingle();
    if (existing) {
      await supabase.from("usage_counters")
        .update({ downloads_count: (existing.downloads_count ?? 0) + 1 })
        .eq("workspace_id", active.id).eq("month", month);
    } else {
      await supabase.from("usage_counters")
        .insert({ workspace_id: active.id, month, downloads_count: 1 });
    }
    setDownloadsThisMonth((n) => n + 1);
  }, [active]);

  return {
    excerptCount,
    downloadsThisMonth,
    limits,
    downloadsAtLimit,
    loading,
    refresh: load,
    incrementDownload,
  };
};

